import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface CartItem {
  productId: string;
  quantity: number;
}

const getSessionId = (): string => {
  let sessionId = localStorage.getItem('cart_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    localStorage.setItem('cart_session_id', sessionId);
  }
  return sessionId;
};

export const useCart = () => {
  const [cart, setCart] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const sessionId = getSessionId();

  const loadCartFromDatabase = useCallback(async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('cart_items')
        .select('product_id, quantity')
        .eq('session_id', sessionId);

      if (error) throw error;

      const cartData: Record<string, number> = {};
      data?.forEach((item) => {
        cartData[item.product_id] = item.quantity;
      });

      setCart(cartData);
      localStorage.setItem('cart', JSON.stringify(cartData));
    } catch (error) {
      console.error('Error loading cart from database:', error);
      setCart({});
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const syncToDatabase = useCallback(async (productId: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        await supabase
          .from('cart_items')
          .delete()
          .eq('session_id', sessionId)
          .eq('product_id', productId);
      } else {
        const { data: existing } = await supabase
          .from('cart_items')
          .select('id')
          .eq('session_id', sessionId)
          .eq('product_id', productId)
          .maybeSingle();

        if (existing) {
          await supabase
            .from('cart_items')
            .update({ quantity })
            .eq('session_id', sessionId)
            .eq('product_id', productId);
        } else {
          await supabase
            .from('cart_items')
            .insert({
              session_id: sessionId,
              product_id: productId,
              quantity
            });
        }
      }
    } catch (error) {
      console.error('Error syncing cart to database:', error);
      // Don't throw - allow cart to work offline
    }
  }, [sessionId]);

  const addToCart = useCallback(async (productId: string, quantity: number = 1) => {
    const newCart = { ...cart };
    newCart[productId] = (newCart[productId] || 0) + quantity;

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));

    await syncToDatabase(productId, newCart[productId]);

    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('globalCartUpdate'));
  }, [cart, syncToDatabase]);

  const updateQuantity = useCallback(async (productId: string, quantity: number) => {
    const newCart = { ...cart };

    if (quantity <= 0) {
      delete newCart[productId];
    } else {
      newCart[productId] = quantity;
    }

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));

    await syncToDatabase(productId, quantity);

    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('globalCartUpdate'));
  }, [cart, syncToDatabase]);

  const removeFromCart = useCallback(async (productId: string) => {
    const newCart = { ...cart };
    delete newCart[productId];

    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));

    await syncToDatabase(productId, 0);

    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('globalCartUpdate'));
  }, [cart, syncToDatabase]);

  const clearCart = useCallback(async () => {
    try {
      await supabase
        .from('cart_items')
        .delete()
        .eq('session_id', sessionId);
    } catch (error) {
      console.error('Error clearing cart from database:', error);
      // Continue anyway - clear locally
    }

    setCart({});
    localStorage.setItem('cart', JSON.stringify({}));

    window.dispatchEvent(new Event('cartUpdated'));
    window.dispatchEvent(new Event('globalCartUpdate'));
  }, [sessionId]);

  const getCartCount = useCallback(() => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  }, [cart]);

  useEffect(() => {
    loadCartFromDatabase();
  }, [loadCartFromDatabase]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cart' && e.newValue) {
        setCart(JSON.parse(e.newValue));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartCount
  };
};